import type { BookingPaymentMethod, BookingPreferences, SelectedSlot } from "../bookingFlowMachine"
import { isBookingConflict, isBookingSessionExpired } from "../errorReporting"
import type { BookingPayment } from "../hooks"
import { Alert, BackIcon, Box, Progress, ForwardIcon, Typography, useTheme } from "@carrotfertility/carotene-core"
import { Form, FormButton } from "@carrotfertility/carotene-core-x"
import { BookingCard } from "./BookingCard"
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js"
import { loadStripe, type Stripe, type StripeError, type Appearance } from "@stripe/stripe-js"
import React from "react"
import { Lock } from "lucide-react"

type PaymentPageProps = {
  enabled: boolean
  bookingId: string | null
  alreadyPaymentPreauthorized: boolean
  onBookAppointment: () => Promise<void>
  payment: BookingPayment | null
  paymentMethod: BookingPaymentMethod | null
  preferences: BookingPreferences | null
  slot: SelectedSlot | null
  onPaymentCanceled: () => void
  onBack: () => void
  recoveryAlert?: string
}

const stripeInstances = new Map<string, Promise<Stripe | null>>()
function getStripe(key: string): Promise<Stripe | null> {
  let instance = stripeInstances.get(key)
  if (!instance) {
    // Stripe.js caches load failures until reload; Elements requires a stable promise even on failure.
    instance = loadStripe(key).catch(() => null)
    stripeInstances.set(key, instance)
  }
  return instance
}

export function PaymentPage(props: PaymentPageProps): JSX.Element {
  const {
    enabled,
    bookingId,
    alreadyPaymentPreauthorized,
    onBookAppointment,
    payment,
    paymentMethod,
    preferences,
    slot,
    onBack,
    onPaymentCanceled,
    recoveryAlert
  } = props
  const theme = useTheme()
  const appearance = React.useMemo<Appearance>(
    () => ({
      theme: "stripe",
      labels: "above",
      variables: {
        colorPrimary: theme.palette.primary.main,
        colorBackground: theme.palette.background.paper,
        colorText: theme.palette.text.primary,
        colorTextSecondary: theme.palette.text.secondary,
        colorTextPlaceholder: theme.palette.text.disabled,
        colorDanger: theme.palette.error.main,
        fontFamily: theme.typography.fontFamily,
        fontSizeBase: String(theme.typography.body1.fontSize),
        borderRadius: `${theme.borderRadius(theme.tokens.borderRadius.sm)}px`,
        spacingUnit: theme.spacing(theme.tokens.spacing.xxs)
      },
      rules: {
        ".Label": { fontWeight: "700", marginBottom: theme.spacing(theme.tokens.spacing.xs) },
        ".Input": {
          padding: theme.spacing(theme.tokens.spacing.md),
          border: `1px solid ${theme.palette.text.secondary}`,
          boxShadow: "none"
        },
        ".Input:focus": {
          borderColor: theme.palette.text.primary,
          boxShadow: `0 0 0 1px ${theme.palette.text.primary}`
        },
        ".Input--invalid": { borderColor: theme.palette.error.main }
      }
    }),
    [theme]
  )
  if (
    !enabled ||
    !bookingId ||
    !payment?.publishableKey ||
    !payment?.clientSecret ||
    !paymentMethod ||
    !preferences ||
    !slot?.availableHostIds.length
  ) {
    return (
      <Box>
        <Alert severity="info">
          {enabled && payment && !bookingId
            ? "This payment session is no longer available. Go back and choose your appointment again to start a new payment session."
            : "We couldn’t load the payment form. Go back and try again."}
        </Alert>
        <Form onSubmit={() => undefined}>
          <FormButton type="button" onClick={onBack} variant="text" startIcon={<BackIcon />}>
            Back
          </FormButton>
        </Form>
      </Box>
    )
  }
  const stripePromise = getStripe(payment.publishableKey)
  return (
    <Elements
      key={payment.clientSecret}
      stripe={stripePromise}
      options={{ clientSecret: payment.clientSecret, appearance }}
    >
      <PaymentForm
        payment={payment}
        alreadyPaymentPreauthorized={alreadyPaymentPreauthorized}
        onBookAppointment={onBookAppointment}
        onBack={onBack}
        onPaymentCanceled={onPaymentCanceled}
        recoveryAlert={recoveryAlert}
        stripePromise={stripePromise}
      />
    </Elements>
  )
}

const loadErrorMessage =
  "We couldn’t load the secure payment form. Check your connection and reload this page to try again."
const connectionErrorMessage = "We couldn’t confirm the payment status. Check your connection and try again."
function paymentErrorMessage(error: StripeError): string {
  if (error.type === "card_error" && error.code === "card_declined") {
    return "Your card was declined. Please try a new card or contact your card issuer for more support."
  }
  if (error.type === "card_error" || error.type === "validation_error") {
    return error.message || "We couldn’t authorize this card. Check your card details or try another card."
  }
  if (error.type === "api_connection_error") return connectionErrorMessage
  return "Something went wrong. Your appointment wasn’t booked and you haven’t been charged. Please try again."
}

function PaymentForm({
  payment,
  alreadyPaymentPreauthorized,
  onPaymentCanceled,
  onBookAppointment,
  onBack,
  recoveryAlert,
  stripePromise
}: {
  payment: BookingPayment
  alreadyPaymentPreauthorized: boolean
  onBookAppointment: () => Promise<void>
  onPaymentCanceled: () => void
  onBack: () => void
  recoveryAlert?: string
  stripePromise: Promise<Stripe | null>
}): JSX.Element {
  const stripe = useStripe()
  const elements = useElements()
  const submitting = React.useRef(false)
  const paymentPreauthorized = React.useRef(alreadyPaymentPreauthorized)
  const collectsCard = React.useRef(!alreadyPaymentPreauthorized)
  const [pending, setPending] = React.useState(false)
  const [ready, setReady] = React.useState(false)
  const [bookingCompleted, setBookingCompleted] = React.useState(false)
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null)
  const [blocked, setBlocked] = React.useState(false)
  const [backBlocked, setBackBlocked] = React.useState(alreadyPaymentPreauthorized)
  React.useEffect(() => {
    let active = true
    stripePromise.then((loaded) => {
      if (active && !loaded && !paymentPreauthorized.current) {
        setErrorMessage(loadErrorMessage)
        setBlocked(true)
      }
    })
    return () => {
      active = false
    }
  }, [stripePromise])

  const handleSubmit = async () => {
    if (submitting.current || bookingCompleted || blocked) return
    if (!paymentPreauthorized.current && (!stripe || !elements || !ready)) return
    submitting.current = true
    setPending(true)
    setErrorMessage(null)
    try {
      if (paymentPreauthorized.current) {
        await onBookAppointment()
        setBookingCompleted(true)
        return
      }
      if (!stripe || !elements) return
      const result = await stripe.confirmPayment({ elements, redirect: "if_required" })
      // A retry after a lost response can return the already-confirmed intent as an error.
      const paymentIntent =
        result.error?.code === "payment_intent_unexpected_state" ? result.error.payment_intent : result.paymentIntent
      if (result.error && !paymentIntent) {
        setErrorMessage(paymentErrorMessage(result.error))
        return
      }
      if (!paymentIntent) {
        setErrorMessage(connectionErrorMessage)
        return
      }
      if (paymentIntent.status === "requires_capture" || paymentIntent.status === "succeeded") {
        paymentPreauthorized.current = true
        setBackBlocked(true)
        await onBookAppointment()
        setBookingCompleted(true)
      } else {
        const status = paymentIntent.status
        if (status === "canceled") onPaymentCanceled()
        const cannotRetry = status === "processing" || status === "canceled"
        setBlocked(cannotRetry)
        setBackBlocked(status === "processing")
        setErrorMessage(
          status === "processing"
            ? "Your payment is still processing. Don’t submit another payment. Please contact Carrot support before trying again."
            : status === "canceled"
            ? "This payment session is no longer available. Go back and choose your appointment again to start a new payment session."
            : "Your card has not been authorized. Check your payment details and try again."
        )
      }
    } catch (error) {
      if (isBookingSessionExpired(error)) return
      if (paymentPreauthorized.current && isBookingConflict(error)) {
        // A 409 can also mean payment was captured; verify before allowing a new session.
        try {
          const client = stripe ?? (await stripePromise)
          const result = await client?.retrievePaymentIntent(payment.clientSecret)
          if (!result || result.error || !result.paymentIntent) {
            setErrorMessage(connectionErrorMessage)
            return
          }
          const status = result.paymentIntent.status
          if (status === "canceled") {
            setBlocked(true)
            setBackBlocked(false)
            onPaymentCanceled()
            setErrorMessage(
              "This payment session is no longer available. Go back and choose your appointment again to start a new payment session."
            )
            return
          }
          if (status === "succeeded" || status === "processing") {
            setBlocked(true)
            setBackBlocked(true)
            setErrorMessage(
              "This payment has completed or is still processing, but your appointment has not been booked. Please contact Carrot support before trying again."
            )
            return
          }
        } catch {
          setErrorMessage(connectionErrorMessage)
          return
        }
      }
      if (!paymentPreauthorized.current) setErrorMessage(connectionErrorMessage)
    } finally {
      submitting.current = false
      setPending(false)
    }
  }

  const loading = !paymentPreauthorized.current && (!stripe || !elements || !ready) && !errorMessage
  const alertMessage = errorMessage ?? recoveryAlert

  return (
    <Box aria-busy={loading}>
      {loading && (
        <Box
          role="status"
          aria-label="Loading payment form"
          sx={(theme) => ({
            display: "flex",
            justifyContent: "center",
            padding: theme.spacing(theme.tokens.spacing.xl)
          })}
        >
          <Progress aria-hidden="true" />
        </Box>
      )}
      {/* Keep Stripe mounted so its ready event can fire without displaying a partial form. */}
      <Box
        aria-hidden={loading}
        sx={{
          visibility: loading ? "hidden" : "visible",
          blockSize: loading ? 0 : "auto",
          overflow: loading ? "hidden" : "visible"
        }}
      >
        <Form onSubmit={handleSubmit}>
          <BookingCard>
            <Typography variant="h4" component="h1">
              Add a payment method
            </Typography>
            {alertMessage && <Alert severity="error">{alertMessage}</Alert>}
            {collectsCard.current && (
              <PaymentElement
                onReady={() => setReady(true)}
                onLoadError={() => {
                  setReady(false)
                  if (paymentPreauthorized.current) return
                  setBlocked(true)
                  setErrorMessage(loadErrorMessage)
                }}
              />
            )}
            <Box
              sx={(theme) => ({
                display: "flex",
                alignItems: "flex-start",
                gap: theme.spacing(theme.tokens.spacing.sm),
                padding: theme.spacing(theme.tokens.spacing.md),
                borderRadius: `${theme.borderRadius(theme.tokens.borderRadius.md)}px`,
                backgroundColor: theme.palette.background.default,
                color: theme.palette.text.secondary
              })}
            >
              <Box component="span" sx={{ display: "inline-flex", flexShrink: 0 }}>
                <Lock aria-hidden="true" size={24} strokeWidth={1.5} />
              </Box>
              <Typography variant="body1">
                You&apos;ll be charged{" "}
                {new Intl.NumberFormat("en-US", {
                  style: "currency",
                  currency: payment.patientResponsibility.currency
                }).format(payment.patientResponsibility.amountMinorUnits / 100)}{" "}
                when your appointment is confirmed — not right now.
              </Typography>
            </Box>
            {bookingCompleted && <Alert severity="success">Your appointment is booked.</Alert>}
            <Box
              sx={(theme) => ({
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                marginBlockStart: theme.spacing(theme.tokens.spacing.md),
                gap: theme.spacing(theme.tokens.spacing.sm)
              })}
            >
              <FormButton
                type="button"
                onClick={onBack}
                color="secondary"
                variant="text"
                startIcon={<BackIcon />}
                disabled={pending || bookingCompleted || backBlocked}
              >
                Back
              </FormButton>
              <FormButton
                type="submit"
                color="secondary"
                variant="contained"
                endIcon={<ForwardIcon />}
                disabled={
                  (!paymentPreauthorized.current && (!stripe || !elements || !ready)) ||
                  pending ||
                  bookingCompleted ||
                  blocked
                }
              >
                Confirm payment
              </FormButton>
            </Box>
          </BookingCard>
        </Form>
      </Box>
    </Box>
  )
}
